import { useState, useRef, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { ConversationList, type ConversationListRef } from '@/components/conversation-list';
import { MessageView } from '@/components/message-view';
import { AuthGuard } from '@/components/auth-guard';
import { AppLayout } from '@/components/app-layout';
import { PageFallback } from '@/components/page-fallback';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { brandClient } from '@/lib/brand-client';
import { conversationClient } from '@/lib/gupshup-client';
import { posProvidersClient } from '@/lib/pos-providers-client';
import { discountsEnabledForPosTypes } from '@/lib/discounts-client';
import { getStoredSession } from '@/lib/auth-client';
import { usePendingOrdersCount } from '@/hooks/use-pending-orders-count';
import { useHandoffCount } from '@/hooks/use-handoff-count';
import { useNotificationStream } from '@/hooks/use-notification-stream';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useConversationDraft } from '@/hooks/use-conversation-draft';
import { CartPanel } from '@/components/cart/cart-panel';
import { CartStartPanel } from '@/components/cart/cart-start-panel';
import { ConfirmSummaryDialog } from '@/components/cart/confirm-summary-dialog';
import type { ConfirmDraftResult } from '@/lib/draft-order-client';
import type { LocationResponseDto } from '@/types/brand';

// The conversation view ("/") stays eager — it's the primary surface. The
// rest of the routes load on demand; /settings alone pulls in the 80+ file
// brand-management tree (plus leaflet via its locations tab).
const BrandManagementPage = lazy(() =>
    import('@/components/brand-management/BrandManagementPage').then((m) => ({
        default: m.BrandManagementPage,
    })),
);
const OrdersTab = lazy(() =>
    import('@/components/orders/orders-tab').then((m) => ({ default: m.OrdersTab })),
);
const BankTransfersTab = lazy(() =>
    import('@/components/bank-transfers/bank-transfers-tab').then((m) => ({ default: m.BankTransfersTab })),
);
const TemplateManagementPage = lazy(() =>
    import('@/components/templates/TemplateManagementPage').then((m) => ({
        default: m.TemplateManagementPage,
    })),
);
const DiscountsPage = lazy(() =>
    import('@/components/discounts/discounts-page').then((m) => ({ default: m.DiscountsPage })),
);
const PlaygroundPage = lazy(() =>
    import('@/components/playground/playground-page').then((m) => ({ default: m.PlaygroundPage })),
);

type Conversation = {
    id: string;
    phoneNumber: string;
    contactName?: string;
    aiDisabledUntil?: string;
    handoffUntil?: string;
    /** A DRAFT order is open on this conversation (from the list poll). Drives the cart poll cadence. */
    hasActiveOrder?: boolean;
};

export default function App() {
    return (
        <AuthGuard>
            <AuthenticatedApp />
            <Toaster />
        </AuthGuard>
    );
}

/**
 * Location-scoped sessions (client-level access) see a single-location view:
 * data lists are filtered server-side; here we pin the locations list and show
 * the right config notice per session shape (writes are 403'd anyway).
 *
 * - Shared-line sucursales (no own line): everything is master-managed.
 * - Own-line sucursales: config is inherited but per-field overridable.
 *
 * Banner texts live in the 'app' namespace: scopeBanner.readOnly / scopeBanner.overridable.
 */
function ScopeBanner({ message }: { message: string }) {
    return (
        <div className="border-b border-blue-200 dark:border-blue-800/60 bg-blue-50 dark:bg-blue-950/40 px-4 py-2 text-sm text-blue-800 dark:text-blue-300 shrink-0">
            {message}
        </div>
    );
}

/** How long "no me recuerdes para este chat" keeps the AI-resume offer quiet. */
const AI_RESUME_SNOOZE_MS = 60 * 60 * 1000;

function AuthenticatedApp() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    useNotificationStream();
    const sessionScope = getStoredSession()?.scope ?? null;
    const [selectedConversation, setSelectedConversation] = useState<Conversation>();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [cartOpen, setCartOpen] = useState(false);
    // Held here rather than inside the panel because it has to pause the draft polling, which lives
    // one level up: a refetch landing mid-edit would either clobber the working copy or make the
    // "unsaved changes" banner compare against the wrong baseline.
    const [cartDirty, setCartDirty] = useState(false);
    /**
     * An order the operator just placed whose customer has not been told about it yet.
     *
     * Owned here, not in the cart panel: confirming ends the draft, so the panel unmounts on the very
     * next poll — and with it would go the only copy of the tracking URL and the payment link, which
     * exist nowhere else in the UI. Kept until a message actually goes out (or the operator dismisses
     * it), including across closing the panel and switching conversations, because an order sitting in
     * the POS with a ticking payment link and a silent customer is the one state worth nagging about.
     */
    const [pendingConfirmation, setPendingConfirmation] = useState<{
        conversationId: string;
        customerPhone: string;
        result: ConfirmDraftResult;
    } | null>(null);
    const [summaryOpen, setSummaryOpen] = useState(false);
    const [sendingSummary, setSendingSummary] = useState(false);
    /**
     * A conversation switch held up by unsaved cart edits, waiting on the operator to decide. Carries
     * the navigation intent too, so approving it from the orders page still lands on the inbox.
     */
    const [pendingSwitch, setPendingSwitch] = useState<{
        conversation: Conversation;
        navigateHome: boolean;
    } | null>(null);
    /**
     * Offer to re-enable the AI on a taken-over conversation the operator just walked away from.
     * Non-blocking on purpose — the switch already happened; this dialog acts on the conversation
     * LEFT BEHIND. `customerWaiting` is a best-effort last-message check: it picks the description
     * and, on confirm, whether the agent takes a turn right away (takeover-era customer messages
     * are deliberately never re-batched, so without that turn the agent stays quiet until the
     * customer writes again); null = unknown, treated as "nothing owed".
     */
    const [aiResumePrompt, setAiResumePrompt] = useState<{
        conversation: Conversation;
        customerWaiting: boolean | null;
    } | null>(null);
    /**
     * Takeovers the operator already declined to end, keyed by conversation id → the
     * aiDisabledUntil they declined at. Sending another manual message extends aiDisabledUntil,
     * so the same conversation re-arms naturally; confirming records it too so a stale list row
     * can't re-prompt before the next poll.
     */
    const aiResumeDeclined = useRef(new Map<string, string>());
    /**
     * The stronger "don't remind me for this chat" choice: keyed on conversation id → epoch ms,
     * deliberately NOT on aiDisabledUntil — new operator messages must not re-arm it. Time-boxed
     * so a busy hour can't silence the offer for the rest of the shift.
     */
    const aiResumeSnoozedUntil = useRef(new Map<string, number>());
    const [hasOrdersCapability, setHasOrdersCapability] = useState(false);
    const [bankTransfersEnabled, setBankTransfersEnabled] = useState(false);
    const [brandName, setBrandName] = useState<string>();
    const [locations, setLocations] = useState<LocationResponseDto[]>([]);
    const [capabilitiesLoaded, setCapabilitiesLoaded] = useState(false);
    const [brandId, setBrandId] = useState<string>();
    /**
     * Coupons rollout gate — `undefined` while the brand's POS providers are still loading, so the
     * route can hold instead of bouncing a deep link. See `discountsEnabledForPosTypes`.
     */
    const [couponsEnabled, setCouponsEnabled] = useState<boolean>();
    /**
     * The brand's *enabled* POS provider types — `undefined` until they load, and left that way on
     * failure so the Discounts hub disables authoring without claiming the POS owns promotions.
     */
    const [connectedPosTypes, setConnectedPosTypes] = useState<string[]>();
    const conversationListRef = useRef<ConversationListRef>(null);

    // Agent status state (lifted from child components)
    const [phone, setPhone] = useState<string>();
    const [aiAgentEnabled, setAiAgentEnabled] = useState(false);
    const [aiAgentPaused, setAiAgentPaused] = useState(false);
    const [hasBrand, setHasBrand] = useState(false);

    useEffect(() => {
        // Fetch agent status
        brandClient
            .getStatus()
            .then((status) => {
                setPhone(status.phone);
                setAiAgentEnabled(status.aiAgentEnabled);
                setAiAgentPaused(status.aiAgentPaused);
                setHasBrand(status.hasBrand);
            })
            .catch((err) => {
                console.error('Failed to fetch brand status:', err);
            });

        // Fetch capabilities for orders tab
        brandClient
            .getBrand()
            .then((res) => {
                const capabilities = res.capabilities || [];
                setHasOrdersCapability(capabilities.includes('ORDERS'));
                // Transfers only exist for brands with bank details configured — the backend
                // rejects the whole /bank-transfers surface otherwise (same gate).
                setBankTransfersEnabled(!!res.transactionSettings?.bankTransferDetails);
                if (res.name) setBrandName(res.name);
                setLocations(res.locations || []);
                setBrandId(res.id);
            })
            .catch(() => {
                // No brand or error — capabilities stay false
            })
            .finally(() => {
                setCapabilitiesLoaded(true);
            });
    }, []);

    // Coupons are gated on the brand's POS integration for now (see coupons-client). Kept out of
    // the capabilities effect so the extra request can't delay the orders/transfers routes.
    useEffect(() => {
        if (!brandId) return;
        posProvidersClient
            .getProviders(brandId)
            .then((providers) => {
                const activeTypes = providers.filter((p) => p.enabled).map((p) => p.type);
                setConnectedPosTypes(activeTypes);
                setCouponsEnabled(discountsEnabledForPosTypes(activeTypes));
            })
            .catch(() => {
                setCouponsEnabled(false);
            });
    }, [brandId]);

    /**
     * 'pending' while either gate (ORDERS capability, POS rollout) is still resolving — the
     * Discounts hub leaves the Cupones tab out in that window instead of flashing it in and out.
     */
    const couponsAccess = useMemo<'pending' | 'allowed' | 'denied'>(() => {
        if (!capabilitiesLoaded) return 'pending';
        if (!hasOrdersCapability) return 'denied';
        if (couponsEnabled === undefined) return 'pending';
        return couponsEnabled ? 'allowed' : 'denied';
    }, [capabilitiesLoaded, hasOrdersCapability, couponsEnabled]);

    const { pendingCount: pendingOrdersCount } = usePendingOrdersCount({
        enabled: location.pathname !== '/orders' && hasOrdersCapability,
    });

    const { handoffCount } = useHandoffCount({
        enabled: true,
    });

    const handleToggleAiAgent = useCallback(
        async (paused: boolean, options?: { autoReplyMessage?: string; reactivateInMinutes?: number }) => {
            try {
                const result = await brandClient.toggleAiAgent(paused, options);
                setAiAgentPaused(result.aiAgentPaused);
                toast.success(paused ? t('aiAgent.pausedToast') : t('aiAgent.activatedToast'));
            } catch (err) {
                toast.error(err instanceof Error ? err.message : t('aiAgent.toggleError'));
            }
        },
        [t],
    );

    const handleBrandCreatedOrUpdated = useCallback(() => {
        // Refresh status after brand creation/update
        brandClient
            .getStatus()
            .then((status) => {
                setPhone(status.phone);
                setAiAgentEnabled(status.aiAgentEnabled);
                setAiAgentPaused(status.aiAgentPaused);
                setHasBrand(status.hasBrand);
            })
            .catch(() => {});
    }, []);

    const { draft, startContext, refetch: refetchDraft } = useConversationDraft({
        conversationId: selectedConversation?.id,
        paused: cartDirty,
        // Full cadence only while a cart is believed live or the panel is open; otherwise the poll
        // drops to a slow safety-net tick that merely guards against a stale list flag.
        active: Boolean(selectedConversation?.hasActiveOrder) || cartOpen,
        // What starting a cart would take (suggested branch, recent-order warning) costs the server
        // extra lookups, so only the open panel asks; with a live cart the server skips it anyway.
        includeStartContext: cartOpen,
    });

    // Selecting another conversation must not carry the cart (or a stale dirty flag) across.
    // `pendingConfirmation` is deliberately NOT cleared here — it is keyed by conversation and has to
    // survive the operator looking at something else before they get round to notifying the customer.
    useEffect(() => {
        setCartOpen(false);
        setCartDirty(false);
        setSummaryOpen(false);
    }, [selectedConversation?.id]);

    /**
     * The selected conversation is a snapshot taken at click time; the list re-polls every 2s and is
     * the only thing that re-reads `hasActiveOrder`. Adopt just that flag from the fresh row — it
     * paces the cart poll above, and a stale `false` left the header stuck on "start order" for up to
     * 30s after the agent had already opened a draft. Identity is preserved when nothing changed, so
     * the routine 2s callback re-renders nothing.
     */
    const handleSelectedConversationRefreshed = useCallback((fresh: Conversation) => {
        setSelectedConversation((prev) =>
            prev && prev.id === fresh.id && prev.hasActiveOrder !== fresh.hasActiveOrder
                ? { ...prev, hasActiveOrder: fresh.hasActiveOrder }
                : prev,
        );
    }, []);

    // The flag just flipped on (the agent opened a draft): fetch the cart now instead of waiting out
    // the current poll tick, so the header button moves as soon as the list knows.
    useEffect(() => {
        if (selectedConversation?.hasActiveOrder && !draft && !cartDirty) void refetchDraft();
    }, [selectedConversation?.hasActiveOrder, draft, cartDirty, refetchDraft]);

    /**
     * After any panel action that changes conversation state: re-read the cart and the conversation
     * row, because a takeover or hand-back moves `aiDisabledUntil` and that is what decides whether
     * the panel is editable at all.
     */
    const handleCartChanged = async () => {
        // The conversation FIRST, then the cart. Both land either way, but a cart that appears before
        // its conversation's new pause does makes the panel flash its read-only "Tomar control" state
        // on the way in — which is exactly what an operator who just started the order did not do.
        if (selectedConversation?.phoneNumber) {
            await handleTemplateSent(selectedConversation.phoneNumber);
        }
        await refetchDraft();
    };

    const handleConfirmed = (result: ConfirmDraftResult) => {
        if (!selectedConversation?.id) return;
        setPendingConfirmation({
            conversationId: selectedConversation.id,
            customerPhone: selectedConversation.phoneNumber,
            result,
        });
        setSummaryOpen(true);
        setCartOpen(false);
    };

    const handleSendSummary = async (message: string) => {
        if (!pendingConfirmation) return;

        setSendingSummary(true);
        try {
            await conversationClient.messages.sendText({
                to: pendingConfirmation.customerPhone,
                body: message,
                role: 'assistant',
            });
            // Only now: while the send is failing, the customer still has not been told, so the
            // reminder has to stay exactly where it was.
            setPendingConfirmation(null);
            setSummaryOpen(false);
            await handleTemplateSent(pendingConfirmation.customerPhone);
        } catch {
            toast.error(t('conversations:cart.errors.sendFailed'));
        } finally {
            setSendingSummary(false);
        }
    };

    /** The order the currently-selected conversation still owes its customer a message about. */
    const unsentConfirmation =
        pendingConfirmation && pendingConfirmation.conversationId === selectedConversation?.id
            ? pendingConfirmation
            : null;

    /** The operator holds the floor exactly while the conversation's AI pause is still in effect. */
    const hasTakenOver = Boolean(
        selectedConversation?.aiDisabledUntil && new Date(selectedConversation.aiDisabledUntil) > new Date(),
    );

    /**
     * The branches this session may act for — its own sucursal when it is scoped to one, the whole
     * brand otherwise. Shared by the orders filter and the cart's branch picker so the two can never
     * disagree about which branches this operator is allowed to place for.
     */
    const scopedLocations = useMemo(
        () =>
            sessionScope?.locationId
                ? locations.filter((location) => location.id === sessionScope.locationId)
                : locations,
        [locations, sessionScope?.locationId],
    );
    const cartBranches = useMemo(
        () => scopedLocations.map((location) => ({ id: location.id, name: location.name })),
        [scopedLocations],
    );

    const handleTemplateSent = async (phoneNumber: string) => {
        const conversations = await conversationListRef.current?.refresh();
        if (conversations) {
            const conversation = conversations.find((conv) => conv.phoneNumber === phoneNumber);
            if (conversation) {
                setSelectedConversation(conversation);
            }
        }
    };

    /**
     * Leaving the conversation unmounts the cart panel and takes its staged edits with it, so a dirty
     * cart intercepts the switch rather than silently discarding work. Returns whether the caller may
     * proceed; when it may not, the confirmation below owns the decision.
     *
     * Only a genuine change of conversation counts — re-selecting the one already open is not a switch,
     * and the polled list re-selects the current conversation routinely.
     */
    const mayLeaveConversation = (next: Conversation, navigateHome = false): boolean => {
        if (!cartDirty || next.id === selectedConversation?.id) return true;
        setPendingSwitch({ conversation: next, navigateHome });
        return false;
    };

    /**
     * The operator is walking away from `previous` — if they still hold the floor there (active
     * takeover) and the brand's AI could actually answer, offer to hand the conversation back to
     * the agent. Offered, never automatic: the AI only comes back when the operator says so.
     */
    const maybeOfferAiResume = (previous: Conversation | undefined) => {
        if (!previous?.aiDisabledUntil) return;
        if (new Date(previous.aiDisabledUntil) <= new Date()) return;
        // Re-enabling per-conversation AI is an empty promise while the brand agent is off/paused.
        if (!hasBrand || !aiAgentEnabled || aiAgentPaused) return;
        const snoozedUntil = aiResumeSnoozedUntil.current.get(previous.id);
        if (snoozedUntil !== undefined && snoozedUntil > Date.now()) return;
        if (aiResumeDeclined.current.get(previous.id) === previous.aiDisabledUntil) return;
        setAiResumePrompt({ conversation: previous, customerWaiting: null });
        // Best-effort: does the customer have the last word? Failure just means "unknown".
        void conversationClient.messages
            .listByConversation({ conversationId: previous.id, limit: 1 })
            .then((response) => {
                const newest = response.data[0];
                if (!newest) return;
                setAiResumePrompt((current) =>
                    current?.conversation.id === previous.id
                        ? { ...current, customerWaiting: newest.direction === 'incoming' }
                        : current,
                );
            })
            .catch(() => {});
    };

    const declineAiResume = () => {
        if (!aiResumePrompt) return;
        const { conversation } = aiResumePrompt;
        if (conversation.aiDisabledUntil) {
            aiResumeDeclined.current.set(conversation.id, conversation.aiDisabledUntil);
        }
        setAiResumePrompt(null);
    };

    /** "Ahora no" asks again once the operator re-engages; this stays quiet for a full hour. */
    const snoozeAiResume = () => {
        if (!aiResumePrompt) return;
        aiResumeSnoozedUntil.current.set(aiResumePrompt.conversation.id, Date.now() + AI_RESUME_SNOOZE_MS);
        setAiResumePrompt(null);
    };

    const confirmAiResume = () => {
        const prompt = aiResumePrompt;
        if (!prompt) return;
        setAiResumePrompt(null);
        const { conversation, customerWaiting } = prompt;
        if (conversation.aiDisabledUntil) {
            aiResumeDeclined.current.set(conversation.id, conversation.aiDisabledUntil);
        }
        const name = conversation.contactName || conversation.phoneNumber || conversation.id;
        void conversationClient.conversations
            .resumeAi(conversation.id, { requestAgentTurn: customerWaiting === true })
            .then((result) => {
                void conversationListRef.current?.refresh();
                toast.success(
                    result.agentTurnQueued
                        ? t('conversations:aiResumePrompt.resumedWithTurn', { name })
                        : t('conversations:aiResumePrompt.resumed', { name }),
                );
            })
            .catch(() => toast.error(t('conversations:aiResumePrompt.error')));
    };

    const handleSelectConversation = (conversation: Conversation) => {
        if (!mayLeaveConversation(conversation)) return;
        if (conversation.id !== selectedConversation?.id) maybeOfferAiResume(selectedConversation);
        setSelectedConversation(conversation);
        setSidebarOpen(false);
    };

    const handleViewConversationFromOrder = (conversationId: string) => {
        const target: Conversation = { id: conversationId, phoneNumber: '' };
        if (!mayLeaveConversation(target, true)) return;
        if (target.id !== selectedConversation?.id) maybeOfferAiResume(selectedConversation);
        setSelectedConversation(target);
        navigate('/');
    };

    /** They chose to drop the staged edits. */
    const confirmPendingSwitch = () => {
        if (!pendingSwitch) return;
        // Cleared here as well as by the effect keyed on the conversation id: the guard reads this flag,
        // and leaving it set would make it fire again on the very switch it just approved.
        setCartDirty(false);
        // The guard only ever holds a GENUINE switch (same-id re-selects pass through it), so the
        // conversation being left behind gets its AI-resume offer here too.
        maybeOfferAiResume(selectedConversation);
        setSelectedConversation(pendingSwitch.conversation);
        setSidebarOpen(false);
        if (pendingSwitch.navigateHome) navigate('/');
        setPendingSwitch(null);
    };

    // Deep links (orders, notifications) only carry the conversation id — hydrate the customer
    // name/phone (and AI/handoff state) from the API so the header doesn't render empty.
    useEffect(() => {
        const conversation = selectedConversation;
        if (!conversation?.id || conversation.phoneNumber) return;
        let cancelled = false;
        conversationClient.conversations
            .get(conversation.id)
            .then((res) => {
                if (cancelled) return;
                setSelectedConversation((prev) =>
                    prev?.id === conversation.id
                        ? {
                              id: conversation.id,
                              phoneNumber: res.data.customerPhone,
                              contactName: res.data.customerName,
                              aiDisabledUntil: res.data.aiDisabledUntil,
                              handoffUntil: res.data.handoffUntil,
                          }
                        : prev,
                );
            })
            .catch((err) => {
                console.error('Failed to load conversation details:', err);
            });
        return () => {
            cancelled = true;
        };
    }, [selectedConversation]);

    // Pick up conversation from navigation state (e.g. from orders)
    useEffect(() => {
        const state = location.state as { conversationId?: string } | null;
        if (state?.conversationId && location.pathname === '/') {
            setSelectedConversation({ id: state.conversationId, phoneNumber: '' });
            // Clear the state so it doesn't re-trigger on future navigations to /
            navigate('/', { replace: true, state: null });
        }
    }, [location, navigate]);

    // Pick up conversation from ?conversationId= query (e.g. from notification click)
    useEffect(() => {
        if (location.pathname !== '/') return;
        const params = new URLSearchParams(location.search);
        const conversationId = params.get('conversationId');
        if (conversationId) {
            setSelectedConversation({ id: conversationId, phoneNumber: '' });
            navigate('/', { replace: true });
        }
    }, [location, navigate]);

    // Leaving the inbox for another page is walking away from the open chat too. Latest-ref
    // pattern so the effect fires only on an actual route change, never on selection changes.
    const inboxExitRef = useRef({ pathname: location.pathname, offer: () => {} });
    inboxExitRef.current.offer = () => maybeOfferAiResume(selectedConversation);
    useEffect(() => {
        const cameFromInbox = inboxExitRef.current.pathname === '/';
        inboxExitRef.current.pathname = location.pathname;
        if (cameFromInbox && location.pathname !== '/') inboxExitRef.current.offer();
    }, [location.pathname]);

    return (
        <AppLayout
            hasOrdersCapability={hasOrdersCapability}
            brandName={brandName}
            phone={phone}
            aiAgentEnabled={aiAgentEnabled}
            aiAgentPaused={aiAgentPaused}
            hasBrand={hasBrand}
            onToggleAiAgent={handleToggleAiAgent}
            pendingOrdersCount={pendingOrdersCount}
            handoffCount={handoffCount}
        >
            <Suspense fallback={<PageFallback />}>
                <Routes>
                    <Route
                        path="/"
                        element={
                            <>
                                <ConversationList
                                    ref={conversationListRef}
                                    onSelectConversation={handleSelectConversation}
                                    selectedConversationId={selectedConversation?.id}
                                    onSelectedConversationRefreshed={handleSelectedConversationRefreshed}
                                    sidebarOpen={sidebarOpen}
                                    onCloseSidebar={() => setSidebarOpen(false)}
                                    handoffCount={handoffCount}
                                    desktopCollapsed={cartOpen && Boolean(selectedConversation?.id)}
                                />
                                <MessageView
                                    key={selectedConversation?.id}
                                    conversationId={selectedConversation?.id}
                                    phoneNumber={selectedConversation?.phoneNumber}
                                    contactName={selectedConversation?.contactName}
                                    aiDisabledUntil={selectedConversation?.aiDisabledUntil}
                                    handoffUntil={selectedConversation?.handoffUntil}
                                    onTemplateSent={handleTemplateSent}
                                    onConversationStateChanged={handleTemplateSent}
                                    onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
                                    draftItemCount={draft ? draft.items.length : undefined}
                                    canStartOrder={hasOrdersCapability}
                                    cartOpen={cartOpen}
                                    onToggleCart={() => setCartOpen((open) => !open)}
                                    hasUnsentConfirmation={Boolean(unsentConfirmation)}
                                    onOpenConfirmationSummary={() => setSummaryOpen(true)}
                                    onDismissConfirmation={() => setPendingConfirmation(null)}
                                />
                                {cartOpen &&
                                    selectedConversation?.id &&
                                    (draft ? (
                                        <CartPanel
                                            key={draft.orderId}
                                            cart={draft}
                                            conversationId={selectedConversation.id}
                                            branches={cartBranches}
                                            hasTakenOver={hasTakenOver}
                                            onDirtyChange={setCartDirty}
                                            onClose={() => setCartOpen(false)}
                                            onCartChanged={handleCartChanged}
                                            onConfirmed={handleConfirmed}
                                        />
                                    ) : (
                                        // Same slot: the operator opens "the cart" and finds either the
                                        // order in progress or the way to begin one. Gated on the
                                        // capability, like the button that opens it.
                                        hasOrdersCapability && (
                                            <CartStartPanel
                                                conversationId={selectedConversation.id}
                                                branches={cartBranches}
                                                startContext={startContext}
                                                hasTakenOver={hasTakenOver}
                                                onClose={() => setCartOpen(false)}
                                                onStarted={handleCartChanged}
                                            />
                                        )
                                    ))}
                            </>
                        }
                    />
                    <Route
                        path="/orders"
                        element={
                            !capabilitiesLoaded ? null : hasOrdersCapability ? (
                                <OrdersTab
                                    brandPhone={phone}
                                    bankTransfersEnabled={bankTransfersEnabled}
                                    locations={scopedLocations}
                                    onViewConversation={handleViewConversationFromOrder}
                                />
                            ) : (
                                <Navigate to="/" replace />
                            )
                        }
                    />
                    <Route
                        path="/bank-transfers"
                        element={
                            !capabilitiesLoaded ? null : hasOrdersCapability && bankTransfersEnabled ? (
                                <BankTransfersTab onViewConversation={handleViewConversationFromOrder} />
                            ) : (
                                <Navigate to="/" replace />
                            )
                        }
                    />
                    <Route
                        path="/templates"
                        element={
                            // Templates are a per-line resource (like flows): own-line
                            // sessions manage their line's templates; shared-line
                            // sessions ride the master's line and only read them.
                            sessionScope && !sessionScope.hasOwnLine ? (
                                <div className="flex-1 flex flex-col min-h-0">
                                    <ScopeBanner message={t('scopeBanner.readOnly')} />
                                    <TemplateManagementPage readOnly />
                                </div>
                            ) : (
                                <TemplateManagementPage />
                            )
                        }
                    />
                    <Route
                        path="/discounts"
                        element={
                            // The hub itself rides ORDERS alone: a POS brand that manages its own
                            // promotions still has to see them here, read-only. The coupons gate
                            // (ORDERS + the per-POS rollout allowlist) applies one level down, to
                            // the Cupones tab, which is why it is passed in rather than checked here.
                            !capabilitiesLoaded ? null : hasOrdersCapability ? (
                                <DiscountsPage couponsAccess={couponsAccess} connectedPosTypes={connectedPosTypes} />
                            ) : (
                                <Navigate to="/" replace />
                            )
                        }
                    />
                    {/* Coupons moved into the hub — keep old links, notifications and bookmarks alive. */}
                    <Route path="/coupons" element={<Navigate to="/discounts" replace />} />
                    <Route path="/playground" element={<PlaygroundPage />} />
                    <Route
                        path="/settings"
                        element={
                            <div className="flex-1 flex flex-col min-h-0 min-w-0">
                                {sessionScope?.readOnly && (
                                    <ScopeBanner
                                        message={
                                            sessionScope.hasOwnLine
                                                ? t('scopeBanner.overridable')
                                                : t('scopeBanner.readOnly')
                                        }
                                    />
                                )}
                                <ErrorBoundary label="Se rompió la configuración">
                                    <BrandManagementPage
                                        onBack={() => navigate('/')}
                                        aiAgentEnabled={aiAgentEnabled}
                                        onBrandChanged={handleBrandCreatedOrUpdated}
                                        sessionScope={sessionScope}
                                    />
                                </ErrorBoundary>
                            </div>
                        }
                    />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </Suspense>

            {/*
                Outside the routes on purpose: the order is already placed, and the operator must be
                able to reach its tracking and payment links whatever they navigate to next. Closing
                the dialog does not discard it — the strip in the conversation brings it back.
            */}
            <ConfirmSummaryDialog
                open={summaryOpen && unsentConfirmation !== null}
                result={unsentConfirmation?.result ?? null}
                submitting={sendingSummary}
                onClose={() => setSummaryOpen(false)}
                onSend={(message) => void handleSendSummary(message)}
            />

            {/*
                Staged cart edits about to be lost. Cancel is the default action — there is no "save
                and switch" because the working copy lives in the panel, and a save can be refused
                (out of zone, a repriced line) in ways that need the operator to be looking at it.
            */}
            <AlertDialog open={pendingSwitch !== null} onOpenChange={(open) => !open && setPendingSwitch(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('conversations:cart.unsavedSwitch.title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('conversations:cart.unsavedSwitch.description')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('conversations:cart.unsavedSwitch.stay')}</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmPendingSwitch}>
                            {t('conversations:cart.unsavedSwitch.discard')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/*
                The chat just left behind is still in manual mode. Offered, never automatic: the AI
                only comes back when the operator says so — and when the customer's last messages
                went unanswered, confirming also has the agent answer them right away
                (requestAgentTurn), since takeover-era messages are never re-batched on their own.
            */}
            <AlertDialog open={aiResumePrompt !== null} onOpenChange={(open) => !open && declineAiResume()}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {t('conversations:aiResumePrompt.title', {
                                name:
                                    aiResumePrompt?.conversation.contactName ||
                                    aiResumePrompt?.conversation.phoneNumber ||
                                    '',
                            })}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {aiResumePrompt?.customerWaiting
                                ? t('conversations:aiResumePrompt.descriptionWaiting')
                                : t('conversations:aiResumePrompt.description')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('conversations:aiResumePrompt.dismiss')}</AlertDialogCancel>
                        {/* Not an AlertDialogCancel: closing through Radix would run declineAiResume
                            (the aiDisabledUntil-keyed dismissal) instead of the 1-hour snooze. */}
                        <Button variant="outline" onClick={snoozeAiResume}>
                            {t('conversations:aiResumePrompt.snooze')}
                        </Button>
                        <AlertDialogAction onClick={confirmAiResume}>
                            {t('conversations:aiResumePrompt.confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </AppLayout>
    );
}
